/**
 * tyt-faq-rail.js
 *
 * The scroll rail under the FAQ category pills. Written 2026-09-09 for Maud:
 * "you can't directly see that you can scroll through them."
 *
 * WHY A DRAWN BAR AND NOT `::-webkit-scrollbar`. Styled native scrollbars show
 * in desktop Chrome — the option mockup had one — but phone browsers keep the
 * native overflow scrollbar hidden until a scroll is already under way, and iOS
 * does not honour the styling at all. The bar has to be visible BEFORE the first
 * swipe, because not knowing the strip scrolls is the entire fault. So the rail
 * is an ordinary element and behaves identically everywhere.
 *
 * FOUR THINGS THAT ARE LOAD-BEARING:
 *
 * 1. IT REMOVES ITSELF WHEN THERE IS NOTHING TO SCROLL. Fewer pills, a wider
 *    phone, or a merchant deleting categories all end with the strip fitting —
 *    and a scroll bar that cannot scroll is worse than none. The section renders
 *    it `hidden` and this decides; Liquid cannot, because it has no pill widths.
 *
 * 2. THE FADE FOLLOWS THE SCROLL POSITION, NOT A FIXED EDGE. A right-only mask
 *    would still be fading the last pill once you had reached the end, which
 *    looks like the same clipping bug it is meant to cure. `data-fade` is
 *    start / end / both / none and the CSS masks accordingly.
 *
 * 3. IT READS LAYOUT IN ONE PLACE. Every measurement happens inside paint(),
 *    called from scroll (passive), resize and load — never interleaved with the
 *    writes, so it cannot thrash layout on a scroll.
 *
 * 4. IT IS PURELY DECORATIVE. `aria-hidden` on the rail: the pills are already
 *    a list of links a screen reader can walk, and a fake scrollbar in the
 *    accessibility tree would only add noise. The rail is not focusable and
 *    nothing depends on it — with JS off the strip scrolls exactly as it does
 *    today and the rail simply never appears.
 */
(function () {
  'use strict';

  var MIN_THUMB = 24; /* px — below this a thumb reads as a dot, not a bar */

  function setup(scope) {
    var rails = (scope || document).querySelectorAll('[data-tyt-faq-rail]');
    for (var i = 0; i < rails.length; i++) init(rails[i]);
  }

  function init(rail) {
    if (rail._tytRail) return;
    var strip = rail.parentElement && rail.parentElement.querySelector('.tyt-faq-pills');
    if (!strip) return;
    rail._tytRail = true;
    rail.setAttribute('aria-hidden', 'true');

    var thumb = rail.querySelector('[data-tyt-faq-rail-thumb]');

    function paint() {
      /* Read everything first. */
      var view = strip.clientWidth;
      var total = strip.scrollWidth;
      var max = total - view;
      var trackW = rail.clientWidth;

      /* Nothing to scroll — or the strip is wrapped/gridded at this width, in
         which case scrollWidth equals clientWidth and there is no rail to draw. */
      if (max <= 1 || view === 0) {
        rail.hidden = true;
        strip.removeAttribute('data-fade');
        return;
      }
      rail.hidden = false;

      var w = Math.max(MIN_THUMB, Math.round(trackW * (view / total)));
      var p = strip.scrollLeft / max;
      if (p < 0) p = 0; else if (p > 1) p = 1;

      thumb.style.width = w + 'px';
      thumb.style.transform = 'translateX(' + (p * (trackW - w)) + 'px)';

      /* 2px of slack so a sub-pixel scrollLeft does not leave a permanent fade. */
      var atStart = strip.scrollLeft <= 2;
      var atEnd = strip.scrollLeft >= max - 2;
      strip.setAttribute('data-fade', atStart ? 'end' : atEnd ? 'start' : 'both');
    }

    strip.addEventListener('scroll', paint, { passive: true });

    var t;
    window.addEventListener('resize', function () {
      clearTimeout(t);
      t = setTimeout(paint, 120);
    });

    /* Fraunces and DM Sans change the pill widths as they swap in, which changes
       scrollWidth — so measure again once the fonts have settled. */
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(paint);
    window.addEventListener('load', paint);
    paint();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setup(); });
  } else {
    setup();
  }
  document.addEventListener('shopify:section:load', function (e) { setup(e.target); });
})();
